/**
 * 装配体 Mate 实体工具。
 *
 * 文件职责：
 * 1. 提供 Mate Definition 内参与组件、参与实体的纯数据解析；
 * 2. 避免 document、solver、controller 各自重复解析 Mate 实体；
 * 3. 不依赖 Viewer、Object3D、BRep sidecar 或 UI 状态。
 */

import type {
  AssemblyMateDefinition,
  MateEntityRef,
} from '../definition';
import { isAssemblyMateAutoAngleReferenceEntity } from '../definition/AssemblyMateAngleReference';

/**
 * 解析 Mate 归属需要考虑的实体。
 *
 * @param mate Mate Definition。
 * @returns 去除空引用后的实体列表。
 */
export function resolveAssemblyMateOwnerEntities(
  mate: AssemblyMateDefinition,
): readonly MateEntityRef[] {
  return [
    mate.firstEntity,
    mate.secondEntity,
    ...(mate.referenceEntity ? [mate.referenceEntity] : []),
  ];
}

/**
 * 解析单条 Mate 参与求解和持久化组件快照的真实组件 ID。
 *
 * 设计说明：
 * Angle 的自动参考实体由求解器派生，不代表真实组件选择；把它纳入组件图会导致
 * 范围序列化和局部重求解误认为存在第三个真实组件，因此这里显式排除。
 *
 * @param mate Mate Definition。
 * @returns 去重后的真实组件实例 ID。
 */
export function resolveAssemblyMateComponentInstanceIds(
  mate: AssemblyMateDefinition,
): readonly string[] {
  const componentIds = [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
    ...(mate.referenceEntity && !isAssemblyMateAutoAngleReferenceEntity(mate.referenceEntity)
      ? [mate.referenceEntity.componentInstanceId]
      : []),
  ];
  return Array.from(new Set(componentIds));
}
